import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/shared/lib/cn';

export interface SegmentedOption<T extends string> {
  value: T;
  label: React.ReactNode;
  icon?: LucideIcon;
}

interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  className?: string;
  size?: 'sm' | 'md';
  /** Opções com largura igual preenchendo a linha (útil no mobile). */
  fill?: boolean;
}

/**
 * Controle segmentado Glass com pílula deslizante.
 *
 * A pílula é medida dentro do próprio controle (offsetLeft/offsetWidth) e
 * anima só no eixo dele. Com `layoutId` (posição na tela) ela "voava" de
 * cima/baixo quando o controle mudava de lugar — diálogo centralizado que
 * muda de altura, rolagem, conteúdo carregando.
 */
export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  className,
  size = 'md',
  fill = false,
}: SegmentedControlProps<T>) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const buttons = useRef(new Map<T, HTMLButtonElement>());
  const [pill, setPill] = useState<{ left: number; width: number } | null>(null);
  // A primeira posição entra sem transição (senão a pílula desliza do zero ao abrir).
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    const active = buttons.current.get(value);
    if (!active) {
      setPill(null);
      return;
    }
    const measure = () => setPill({ left: active.offsetLeft, width: active.offsetWidth });
    measure();
    // Largura muda com fonte carregando, contador no rótulo ou `fill` redimensionando.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(active);
    return () => observer.disconnect();
  }, [value, options.length]);

  useEffect(() => {
    if (!pill || ready) return;
    const frame = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(frame);
  }, [pill, ready]);

  // Com `fill`, todas as opções já cabem — scrollIntoView no documento
  // desloca o dialog/teclado no mobile e prende o foco no input errado.
  useEffect(() => {
    if (fill) return;
    const scroller = scrollerRef.current;
    const active = buttons.current.get(value);
    if (!scroller || !active) return;
    const target = active.offsetLeft - scroller.clientWidth / 2 + active.clientWidth / 2;
    scroller.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
  }, [value, fill]);

  return (
    <div
      ref={scrollerRef}
      className={cn(
        'glass-thin thin-scrollbar relative rounded-xl p-1',
        fill ? 'flex w-full' : 'inline-flex max-w-full overflow-x-auto',
        className,
      )}
    >
      {pill ? (
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute bottom-1 left-0 top-1 rounded-lg bg-primary shadow-soft',
            ready && 'transition-[transform,width] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none',
          )}
          style={{ width: pill.width, transform: `translateX(${pill.left}px)` }}
        />
      ) : null}
      {options.map((opt) => {
        const active = opt.value === value;
        const Icon = opt.icon;
        return (
          <button
            key={opt.value}
            ref={(node) => {
              if (node) buttons.current.set(opt.value, node);
              else buttons.current.delete(opt.value);
            }}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onChange(opt.value)}
            aria-pressed={active}
            className={cn(
              'relative inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              size === 'sm' ? 'px-2.5 py-2 text-xs sm:px-3' : 'px-3.5 py-1.5 text-sm',
              fill ? 'min-w-0 flex-1' : 'shrink-0',
              active ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {Icon ? <Icon className="relative size-4 shrink-0" /> : null}
            <span className="relative truncate">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
